/**
 * CompanyDetailPage Component
 *
 * Full-screen company detail view following TaskDetailPage pattern.
 * Uses URL params for company ID and active tab.
 * Supports keyboard navigation (ESC to close).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompanyDetail } from './hooks';
import {
  CompanyProfileTab,
  CompanyPeopleTab,
  CompanyInsightsTab,
  KnowledgeTab,
  CommunicationTab,
} from './tabs';
import { BlacklistModal } from './modals';
import MergeModal from '@/components/modules/MergeModal';
import type { CompanyDetailTab, BlacklistModalState } from './types';
import { getInitials } from './utils';

const TAB_IDS: CompanyDetailTab[] = ['profile', 'people', 'insights', 'knowledge', 'communication'];

export default function CompanyDetailPage() {
  const { t } = useTranslation('people');
  const { companyId, tab } = useParams<{ companyId: string; tab?: string }>();
  const navigate = useNavigate();
  const activeTab = (tab as CompanyDetailTab) || 'profile';

  // Company data hook
  const {
    company,
    loading,
    error: loadError,
    insights,
    insightsLoading,
    knowledge,
    knowledgeLoading,
    communication,
    communicationLoading,
    suppressedItems,
    refresh,
    refreshKnowledge,
  } = useCompanyDetail(companyId);

  // Local state
  const [error, setError] = useState<string | null>(null);
  const [showMerge, setShowMerge] = useState(false);
  const [blacklistModal, setBlacklistModal] = useState<BlacklistModalState>({
    isOpen: false,
    entityType: 'company',
    entityId: '',
    entityName: '',
  });

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showMerge && !blacklistModal.isOpen) {
        navigate('/people/companies');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showMerge, blacklistModal.isOpen]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    navigate('/people/companies');
  };

  const handleTabChange = (newTab: CompanyDetailTab) => {
    navigate(`/people/companies/${companyId}/${newTab}`);
  };

  const handleNavigateToContact = (contactId: string) => {
    navigate(`/people/contacts/${contactId}`);
  };

  const handleError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  // Suppression modal handler for knowledge tab
  const handleOpenSuppressionModal = async (
    _itemType: 'expertise' | 'fact',
    _itemContent: string,
    itemId: string,
    itemSource: 'email_insights' | 'knowledge_card'
  ) => {
    // For companies, we directly delete knowledge cards
    // Email insights suppression is handled through the API
    if (itemSource === 'knowledge_card' && companyId) {
      if (confirm('Delete this item?')) {
        try {
          const { companiesApi } = await import('@/services/contactsApi');
          await companiesApi.deleteKnowledgeCard(companyId, itemId);
          refreshKnowledge();
        } catch (err) {
          handleError(err instanceof Error ? err.message : 'Failed to delete');
        }
      }
    }
  };

  // Blacklist modal handlers
  const handleOpenBlacklistModal = () => {
    if (!company) return;
    setBlacklistModal({
      isOpen: true,
      entityType: 'company',
      entityId: company.id,
      entityName: company.name,
    });
  };

  const handleBlacklist = async (reason: string) => {
    if (!companyId) return;
    try {
      const { companiesApi } = await import('@/services/contactsApi');
      await companiesApi.update(companyId, { is_blacklisted: true, blacklist_reason: reason });
      refresh();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to blacklist company');
    } finally {
      setBlacklistModal({
        isOpen: false,
        entityType: 'company',
        entityId: '',
        entityName: '',
      });
    }
  };

  const handleRemoveFromBlacklist = async () => {
    if (!companyId) return;
    try {
      const { companiesApi } = await import('@/services/contactsApi');
      await companiesApi.update(companyId, { is_blacklisted: false, blacklist_reason: null });
      refresh();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to remove from blacklist');
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyId) return;
    if (!confirm(t('company_detail.delete_confirmation'))) return;
    try {
      const { companiesApi } = await import('@/services/contactsApi');
      await companiesApi.delete(companyId);
      navigate('/people/companies');
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to delete company');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not found
  if (!company) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{t('company_detail.not_found')}</p>
        <button
          onClick={handleClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('company_detail.back_to_companies')}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-200 bg-white">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('company_detail.close_esc')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Logo */}
              <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-gray-600 text-lg font-medium">
                {company.logo_url ? (
                  <img src={company.logo_url} alt="" className="w-full h-full rounded object-cover" />
                ) : (
                  getInitials(company.name)
                )}
              </div>

              {/* Name and domain */}
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{company.name}</h1>
                {company.domain && (
                  <p className="text-sm text-gray-500">{company.domain}</p>
                )}
              </div>

              {/* Badges */}
              <div className="flex gap-2">
                {company.industry && (
                  <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                    {company.industry}
                  </span>
                )}
                {company.is_blacklisted && (
                  <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                    {t('contact_detail.badges.blacklisted')}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMerge(true)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('company_detail.actions.merge')}
              </button>
              {!company.is_blacklisted && (
                <button
                  onClick={handleOpenBlacklistModal}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('company_detail.actions.blacklist')}
                </button>
              )}
              <button
                onClick={handleDeleteCompany}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                {t('company_detail.actions.delete')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-4">
            {TAB_IDS.map((tabId) => (
              <button
                key={tabId}
                onClick={() => handleTabChange(tabId)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tabId
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t(`company_detail.tabs.${tabId}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Error banner */}
      {(error || loadError) && (
        <div className="flex-shrink-0 px-6 py-3 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error || loadError}</p>
        </div>
      )}

      {/* Blacklist banner */}
      {company.is_blacklisted && (
        <div className="flex-shrink-0 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-red-800">{t('company_detail.blacklist_banner.title')}</h4>
              {company.blacklist_reason && (
                <p className="text-sm text-red-700 mt-1">
                  {t('company_detail.blacklist_banner.reason_label')} {company.blacklist_reason}
                </p>
              )}
              {company.blacklisted_at && (
                <p className="text-xs text-red-600 mt-1">
                  {t('company_detail.blacklist_banner.since_label')} {new Date(company.blacklisted_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              onClick={handleRemoveFromBlacklist}
              className="px-3 py-1.5 text-sm bg-white text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors"
            >
              {t('company_detail.blacklist_banner.remove_button')}
            </button>
          </div>
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
        {activeTab === 'profile' && (
          <CompanyProfileTab
            company={company}
            onUpdate={refresh}
            onError={handleError}
          />
        )}
        {activeTab === 'people' && (
          <CompanyPeopleTab
            contacts={company.contacts || []}
            companyId={company.id}
            onUpdate={refresh}
            onError={handleError}
            onNavigateToContact={handleNavigateToContact}
          />
        )}
        {activeTab === 'insights' && (
          <CompanyInsightsTab
            insights={insights}
            loading={insightsLoading}
            onNavigateToContact={handleNavigateToContact}
          />
        )}
        {activeTab === 'knowledge' && (
          <KnowledgeTab
            cards={knowledge}
            loading={knowledgeLoading}
            entityId={company.id}
            entityType="company"
            insights={null}
            suppressedItems={suppressedItems}
            onRefresh={refreshKnowledge}
            onOpenSuppressionModal={handleOpenSuppressionModal}
            onError={handleError}
          />
        )}
        {activeTab === 'communication' && (
          <CommunicationTab
            data={communication}
            loading={communicationLoading}
          />
        )}
      </div>

      {/* Modals */}
      {showMerge && (
        <MergeModal
          isOpen={showMerge}
          onClose={() => setShowMerge(false)}
          entityType="company"
          sourceEntity={company}
          onMergeComplete={() => {
            setShowMerge(false);
            navigate('/people/companies');
          }}
        />
      )}

      <BlacklistModal
        state={blacklistModal}
        onConfirm={handleBlacklist}
        onCancel={() => setBlacklistModal({
          isOpen: false,
          entityType: 'company',
          entityId: '',
          entityName: '',
        })}
      />
    </div>
  );
}
